// Reusable club "wall board" event title. The stored event name may span
// several lines (the create form stacks it, and often bakes the date into the
// last line); this renders each name line as its own centered row, the ordinal
// date range below in gold, and an optional label ("Registration") as a
// subtitle. Every surface that shows the event title uses this so the 3-row
// layout is identical everywhere.

const GREEN = '#123D26'
const GOLD = '#123D26'

const BOARD_MONTHS = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
const ordinalDay = (n) => {
  const v = n % 100
  const suffix = v >= 11 && v <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] || 'th')
  return `${n}${suffix}`
}
const parseLocalDate = (iso) => {
  if (!iso) return null
  const [y, m, d] = String(iso).split('-').map(Number)
  return (y && m && d) ? new Date(y, m - 1, d) : null
}
// "July 30th – Aug 2nd, 2026" from two ISO dates. Collapses the month inside a
// single month; shows the year once at the end.
export function formatEventDateRange(startISO, endISO) {
  const s = parseLocalDate(startISO)
  if (!s) return ''
  const startStr = `${BOARD_MONTHS[s.getMonth()]} ${ordinalDay(s.getDate())}`
  const e = parseLocalDate(endISO)
  if (!e) return `${startStr}, ${s.getFullYear()}`
  const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()
  const endStr = sameMonth ? ordinalDay(e.getDate()) : `${BOARD_MONTHS[e.getMonth()]} ${ordinalDay(e.getDate())}`
  return `${startStr} – ${endStr}, ${e.getFullYear()}`
}

const looksLikeDate = (s) => /\b(Jan|Feb|March|April|May|June|July|Aug|Sept|Oct|Nov|Dec)\b/i.test(s) && /(\d{4}|[–-])/.test(s)

// Split a stored event into its name rows + a single date row. If the name's
// last line is (or looks like) the date, it's peeled off so it can render as
// the gold date row rather than a green name row.
export function splitEventTitle(info) {
  const rawLines = String(info?.name || '').split('\n').map((l) => l.trim()).filter(Boolean)
  const structured = formatEventDateRange(info?.date, info?.endDate)
  const last = rawLines[rawLines.length - 1]
  if (rawLines.length > 1 && last && (last === structured || looksLikeDate(last))) {
    return { nameLines: rawLines.slice(0, -1), dateLine: structured || last }
  }
  return { nameLines: rawLines, dateLine: structured }
}

export default function EventTitle({
  info,
  label,
  className = '',
  nameSize = 20,
  dateSize = 14,
  nameColor = GREEN,
  dateColor = GOLD,
}) {
  const { nameLines, dateLine } = splitEventTitle(info)
  return (
    <div className={`text-center ${className}`}>
      <div style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, color: nameColor, fontSize: nameSize, lineHeight: 1.3 }}>
        {nameLines.length ? nameLines.map((l, i) => <div key={i}>{l}</div>) : <div>Event name…</div>}
      </div>
      {dateLine ? (
        <div className="mt-1" style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: dateColor, fontSize: dateSize, letterSpacing: 0.5 }}>
          {dateLine}
        </div>
      ) : null}
      {label ? (
        <div className="mt-1.5 text-sm font-bold uppercase tracking-widest" style={{ color: 'rgba(18, 61, 38,0.55)' }}>
          {label}
        </div>
      ) : null}
    </div>
  )
}
